<template>
  <Spinner v-if="isLoading" :isSmall="true" :spinnerStyle="'margin: 20px auto'"/>
  <div class="datatable-wrapper" v-else-if="!isLoading && positions?.length">
    <table
        class="p-datatable p-datatable-striped p-datatable-sm p-datatable-hoverable-rows positions-table"
        :class="{ 'datatable--small-text': isSpoilerTable, 'datatable-is-loading': dataTableIsLoading }">
      <thead class="p-datatable-tbody" :class="{ 'p-datatable-thead': !isSpoilerTable }">
      <tr class="table-opened-info__header-row">
        <th>{{ $t('Supplier') }}</th>
        <th>{{ $t('Code') }}</th>
        <th>{{ $t('Name') }}</th>
        <th>{{ $t('Qty') }}</th>
        <th>{{ $t('purchasePriceShortLabel') }}</th>

        <template v-if="!isWarrantyPanel">
          <th>{{ $t('sellingPriceShortLabel') }}</th>
          <th>{{ $t('discountShortLabel') }}</th>
          <th>{{ $t('Amount') }}</th>
          <th>{{ $t('Tax') }}</th>
          <th>{{ $t('Total') }}</th>
          <th>{{ $t('markupPercentLabel') }} %</th>
          <th>{{ $t('marginLabel') }} %</th>
          <th>{{ $t('grossProfitLabel') }}</th>
        </template>
        <th style="width: 30px"></th>
      </tr>
      </thead>

      <tbody class="p-datatable-tbody">
      <tr v-for="(position, index) of positions" :key="position.id ?? index">
        <td>
          <span v-if="+position.type === 1 && getPositionMasterRows(position).length">
            {{ getPositionMasterLabel(position) }}
          </span>

                  <span v-else-if="+position.type === 2 && position.supplier">
            {{ position.supplier.company_name }}
          </span>
          <i v-else class="ti-minus"/>
        </td>

        <td>
          <span v-if="position.type === 1 && position.service?.code">
            {{ position.service.code }}
          </span>
          <span v-else-if="position.code">
            {{ position.code }}
          </span>
          <i v-else class="ti-minus"/>
        </td>

<!--        <td>{{ position.name }}</td>-->

        <td>
          <span class="word-break">
            {{ position.name }}
          </span>

          <div v-if="Number(position.type) === 1 &&
              hasCustomPositionServiceMeta(position)"
              class="position-service-meta p-text-secondary">
            <span v-if="positionHasManualWorkTime(position)"
                 class="p-d-flex p-ai-center">
              <i class="ti-time p-mr-1"/>
              {{ formatPositionWorkTime(position) }}
            </span>

            <span v-if="positionHasManualCustomerQty(position)"
                  class="p-d-flex p-ai-center">
              <i class="ti-receipt p-mr-1"/>
              {{ $t('customerQtyLabel') }}:
              {{ formatPositionCustomerQty(position) }}
            </span>
          </div>
        </td>

        <td>{{ formatDecimal(position.qty) }}</td>
<!--        <td>{{ formatCurrency(position.purchase_price, 2, 4) }}</td>-->

        <td :class="{'warning-color':!isWarrantyPanel &&isPositionSellingPriceLowerThanPurchase(position)}">
          {{ formatCurrency(position.purchase_price, 2, 4) }}
        </td>

        <template v-if="!isWarrantyPanel">
<!--          <td>{{ formatCurrency(position.sell_price, 2, 4) }}</td>-->

          <td :class="{'warning-color':isPositionSellingPriceLowerThanPurchase(position)}">
            {{ formatCurrency(position.sell_price, 2, 4) }}
          </td>

          <td>
            <span v-if="position.discount && +position.discount">
              {{ formatDecimal(position.discount) }}%
            </span>
            <i v-else class="ti-minus"/>
          </td>

          <td>{{ formatCurrency(calculatePositionNetSum(position)) }}</td>

          <td>
            <span v-if="position.tax?.value">{{ formatDecimal(position.tax.value) }}%</span>
            <i v-else class="ti-minus"/>
          </td>

          <td>{{ formatCurrency(calculatePositionTotal(position)) }}</td>

          <td>
            <template v-if="markupsByKey.has(markupKey(position))">
              <span>{{ markupsByKey.get(markupKey(position)) }}</span>
            </template>
            <i v-else class="ti-minus"/>
          </td>

          <td>
            <template v-if="marginsByKey.has(marginKey(position))">
              <span>{{ marginsByKey.get(marginKey(position)) }}</span>
            </template>
            <i v-else class="ti-minus"/>
          </td>

          <td>{{ formatCurrency(calculatePositionProfit(position)) }}</td>
        </template>
        <td>
<!--            <span v-if="position.order?.uid" class="p-d-flex p-ai-center">-->
<!--              <router-link :to="{ path: `/orders/${position.order.uid}` }" target="_blank" draggable="false">-->
<!--                <Button class="p-button-text p-p-1" v-tooltip.top="position.order?.number ? position.order.number : null">-->
<!--                  <i class="ti-hummer"/>-->
<!--                </Button>-->
<!--              </router-link>-->
<!--            </span>-->
<!--          <i v-else class="ti-minus"/>-->
          <OpenOrderButton :position="position"/>
        </td>
      </tr>
      </tbody>
    </table>

    <OwnPaginator v-show="positions?.length"
                  :rows="pagination.perPage"
                  :currentPage="pagination.currentPage - 1"
                  :totalPages="pagination.pageCount"
                  :totalRecords="pagination.totalCount"
                  :rowsPerPageOptions="[5,10,25,50,100]"
                  :pageLinkSize="11"
                  :changePaginationRoute="false"
                  @page="changePage"
                  @change-per-page="changePerPage"/>
  </div>

  <div v-else :class="{ 'datatable--small-text': isSpoilerTable }" style="text-align: center; padding: 35px;">
    {{ $t('No data available' )}}
  </div>
</template>

<script>
import formatMixins from '@/mixins/formatMixins'
import httpClient from "@/services/http.services";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import calculateMixins from "@/mixins/calculateMixins";
import OpenOrderButton from "@/components/OpenOrderButton.vue";

export default {
  components: {OpenOrderButton},
  emits: ['update-items'],
  mixins: [formatMixins, showErrorsMixins, calculateMixins],
  name: 'ManagerSalesPositionsTable',
  props: {
    type: {
      type: Number,
      default: null
    },
    managerId: {
      type: Number,
      default: null
    },
    isShown: {
      type: Boolean,
      default: false,
    },
    isWarrantyPanel: {
      type: Boolean,
      default: false,
    },
    isSpoilerTable: {
      type: Boolean,
      default: true
    },
    fromTimestamp: {
      type: Number,
      default: null
    },
    toTimestamp: {
      type: Number,
      default: null
    },
    searchData: {
      type: String,
      default: ''
    },
  },
  watch: {
    isShown(value) {
      if (value) {
        this.isLoading = true
        this.getPositions(this.type)
      } else {
        this.pagination.currentPage = 1
        this.pagination.perPage = 25
        this.positions = []
        this.isLoading = true
      }
    },
  },
  data() {
    return {
      positions: [],
      dataTableIsLoading: false,
      isLoading: true,
      pagination: {
        totalCount: null,
        pageCount: null,
        currentPage: 1,
        perPage: 25
      },
    }
  },
  computed: {
    markupsByKey() {
      const map = new Map()

      for (const p of (this.positions ?? [])) {
        const key = this.markupKey(p)
        const val = this.calculateMarkup(p)

        if (val != null && val !== '') {
          map.set(key, val)
        }
      }

      return map
    },

    marginsByKey() {
      const map = new Map()

      for (const p of (this.positions ?? [])) {
        const key = this.marginKey(p)
        const val = this.calculateMargin(p)

        if (val != null && val !== '') {
          map.set(key, val)
        }
      }

      return map
    },
  },
  methods: {
    markupKey(p) {
      return p?.id ?? p?.sorting ?? `${p?.type}-${p?.code}-${p?.name}`
    },

    marginKey(p) {
      return p?.id ?? p?.sorting ?? `${p?.type}-${p?.code}-${p?.name}`
    },

    calculatePositionNetSum(position) {
      if (!position) return 0

      const sell = +position.sell_price || 0
      const qty = +position.qty || 0
      const discount = +position.discount || 0

      return (sell - ((discount / 100) * sell)) * qty
    },

    calculatePositionTotal(position) {
      if (!position) return 0

      const sum = this.calculatePositionNetSum(position)
      const tax = +position.tax?.value || 0

      return sum * ((tax / 100) + 1)
    },

    changePage(data) {
      if (this.pagination.currentPage === data.page + 1) return false
      this.pagination.currentPage = data.page + 1
      this.getPositions(this.type)
    },

    changePerPage(perPage) {
      if (this.pagination.perPage === perPage) return false
      this.pagination.perPage = perPage
      this.pagination.currentPage = 1
      this.getPositions(this.type)
    },

    async getPositions(type) {
      if (this.dataTableIsLoading) return
      if (type === null || type === undefined || !this.managerId) return
      this.dataTableIsLoading = true

      const obj = {
        type,
        showWarrantyOnly: this.isWarrantyPanel,
        userId: this.managerId,
        fromTimestamp: this.fromTimestamp,
        toTimestamp: this.toTimestamp,
        page: this.pagination.currentPage,
        'per-page': this.pagination.perPage,
        search: this.searchData ? this.searchData.trim() : null,
      }

      try {
        const { status, data, headers } = await httpClient.post('statistics/get-user-positions?expand=order', obj)

        if (status === 200 && data && Array.isArray(data)) {
          this.pagination.totalCount = +headers['x-pagination-total-count']
          this.pagination.pageCount = +headers['x-pagination-page-count']
          this.positions = data

          if (!this.positions?.length) {
            this.pagination.pageCount = 1
          }
        } else {
          this.positions = []
          this.showUnknownError()
        }
      } catch(err) {
        this.positions = []
        this.showError(err)
      } finally {
        this.dataTableIsLoading = false
        this.isLoading = false
      }
    },
    getMasterFullName(master) {
      if (!master) return ''

      if (master.full_name) {
        return master.full_name
      }

      return `${master.first_name || ''} ${master.last_name || ''}`.trim()
    },

    getMasterShortName(master) {
      if (!master) return ''

      const firstName = master.first_name || ''
      const lastName = master.last_name || ''

      if (firstName && lastName) {
        return `${firstName.charAt(0)}. ${lastName}`
      }

      if (master.full_name) {
        const parts = master.full_name.trim().split(/\s+/)

        if (parts.length >= 2) {
          return `${parts[0].charAt(0)}. ${parts.slice(1).join(' ')}`
        }

        return master.full_name
      }

      return firstName || lastName || ''
    },

    formatSharePercent(value) {
      const num = Number(value)

      if (!Number.isFinite(num)) return ''

      return Number.isInteger(num) ? String(num) : num.toFixed(2)
    },

    positionHasManualWorkTime(position) {
      return (
          !!position &&
          Number(position.type) === 1 &&
          position.work_time !== null &&
          position.work_time !== undefined
      )
    },

    positionHasManualCustomerQty(position) {
      return (
          !!position &&
          Number(position.type) === 1 &&
          position.customer_qty !== null &&
          position.customer_qty !== undefined
      )
    },

    hasCustomPositionServiceMeta(position) {
      return (
          this.positionHasManualWorkTime(position) ||
          this.positionHasManualCustomerQty(position)
      )
    },

    formatPositionWorkTime(position) {
      return this.formatHoursAndMinutes(
          Number(position.work_time ?? 0)
      )
    },

    formatPositionCustomerQty(position) {
      return this.formatDecimal(
          Number(position.customer_qty ?? position.qty ?? 0)
      )
    },

    getPositionMasterRows(position) {
      const rows = Array.isArray(position?.positionMasters)
          ? position.positionMasters
          : []

      if (rows.length) {
        return rows
            .map(item => {
              const master = item.master || null
              const userId = +(item.user_id || master?.id || 0)

              if (!userId) return null

              return {
                user_id: userId,
                master,
                share_percent: Number(item.share_percent ?? 0),
              }
            })
            .filter(Boolean)
      }

      if (position?.master?.id) {
        return [
          {
            user_id: +position.master.id,
            master: position.master,
            share_percent: 100,
          },
        ]
      }

      return []
    },

    getPositionMasterLabel(position) {
      const rows = this.getPositionMasterRows(position)

      if (!rows.length) return ''

      if (rows.length === 1) {
        const item = rows[0]
        const masterName = this.getMasterFullName(item.master)

        if (!masterName) {
          return ''
        }

        return Number(item.share_percent) === 100
            ? masterName
            : `${masterName} ${this.formatSharePercent(item.share_percent)}%`
      }

      return rows
          .map(item => {
            const masterName = this.getMasterShortName(item.master)
            const percent = this.formatSharePercent(item.share_percent)

            return masterName
                ? `${masterName} ${percent}%`
                : `${percent}%`
          })
          .join(' · ')
    },
  }
}
</script>

<style scoped lang="scss">
.datatable-wrapper {
  overflow-y: auto;
}

.positions-table {
  border-collapse: collapse;
  width: 100%;

  tr {
    .small-width {
      width: 6%;
    }

    .medium-width {
      width: 10%;
    }

    th {
      padding: 10px 7px;
    }

    td {
      padding: 7px;
      height: 45px;
    }
  }
}

.data-custom-padding {
  padding: 3px !important;
}

.datatable--small-text {
  font-size: 0.92em;
}

.no-bottom-border {
  border-bottom: none !important;
}

.position-service-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 0.75rem;
  row-gap: 0.15rem;

  margin-top: 3px;

  font-size: 0.9em;
  line-height: 1.25;
}
</style>
